import React from 'react';
import './ServiceAreas.css';
import ghat from "../assets/maid-ghatkoper.png"
import Andheri from "../assets/Andheri.png"

/**
 * Tanya Maid Service — "Service Areas" section
 *
 * Renders a responsive grid of 8 location cards (4 x 2 desktop,
 * 2 x 4 tablet, 1 x 8 mobile). Card data lives in SERVICE_AREAS below —
 * edit that array to add, remove, or reorder locations.
 *
 * Swap `image` for licensed photography of your own team whenever you
 * have it; the placeholders here are royalty-free stock photos used
 * for layout purposes.
 */

const SERVICE_AREAS = [
  {
    id: 'ghatkopar',
    name: 'Ghatkopar',
    description: 'Reliable, background-verified maids for daily and live-in help across Ghatkopar homes.',
    image: ghat,
    featured: true,
  },
  {
    id: 'andheri',
    name: 'Andheri',
    description: 'Trusted domestic help for apartments and villas, matched to your household’s routine.',
    image: Andheri,
    featured: false,
  },
  {
    id: 'powai',
    name: 'Powai',
    description: 'Professional cleaning support for busy Powai families, on the schedule that suits you.',
    image: 'https://images.unsplash.com/photo-1713110824336-f78c320dcf8e?auto=format&fit=crop&w=900&q=80',
    featured: false,
  },
  {
    id: 'bandra',
    name: 'Bandra',
    description: 'Well-trained, courteous maids for premium Bandra homes and residences.',
    image: 'https://images.unsplash.com/photo-1664008760004-182420e58e7c?auto=format&fit=crop&w=900&q=80',
    featured: true,
  },
  {
    id: 'kurla',
    name: 'Kurla',
    description: 'Dependable daily maid service across Kurla, built around your family’s needs.',
    image: 'https://images.unsplash.com/photo-1758272421751-963195322eaa?auto=format&fit=crop&w=900&q=80',
    featured: false,
  },
  {
    id: 'borivali',
    name: 'Borivali',
    description: 'Consistent, trustworthy home help for Borivali households, big or small.',
    image: 'https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?auto=format&fit=crop&w=900&q=80',
    featured: false,
  },
  {
    id: 'thane',
    name: 'Thane',
    description: 'Screened and trained maids serving Thane homes with care and consistency.',
    image: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=900&q=80',
    featured: true,
  },
  {
    id: 'navi-mumbai',
    name: 'Navi Mumbai',
    description: 'Trusted maid service across Navi Mumbai’s residential neighbourhoods.',
    image: 'https://images.unsplash.com/photo-1581578949510-fa7315c4c350?auto=format&fit=crop&w=900&q=80',
    featured: false,
  },
];

function LocationIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 21s-7-6.1-7-11.2A7 7 0 0 1 12 3a7 7 0 0 1 7 6.8C19 14.9 12 21 12 21z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="9.8" r="2.4" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 34C6 20 16 8 34 6c2 18-10 28-24 28-1.4 0-2.8-.1-4-.4z"
        fill="currentColor"
      />
      <path
        d="M8 32C14 24 20 18 30 10"
        stroke="#FBF6F3"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 12h13M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AreaCard({ area, onExplore }) {
  return (
    <div className={`tanya-area-card-wrap${area.featured ? ' tanya-area-card-wrap--featured' : ''}`}>
      <article className="tanya-area-card">
        <div className="tanya-area-image-frame">
          <img
            className="tanya-area-image"
            src={area.image}
            alt={`Professional maid service in ${area.name}, Mumbai`}
            loading="lazy"
          />
          <div className="tanya-area-image-veil" />
          <span className="tanya-area-location-chip">
            <LocationIcon />
            Mumbai
          </span>
          <span className="tanya-area-leaf">
            <LeafIcon />
          </span>
        </div>

        <div className="tanya-area-body">
          <h3 className="tanya-area-heading">Maid Service in {area.name}</h3>
          <p className="tanya-area-desc">{area.description}</p>

          <button
            type="button"
            className="tanya-area-cta"
            onClick={() => onExplore && onExplore(area)}
          >
            Explore Service
            <span className="tanya-area-cta-arrow">
              <ArrowIcon />
            </span>
          </button>
        </div>
      </article>
    </div>
  );
}

export default function ServiceAreas({ onExplore }) {
  return (
    <section className="tanya-area-section" aria-labelledby="tanya-area-title">
      <div className="tanya-area-bg-glow" aria-hidden="true" />
      <div className="tanya-area-bg-glow tanya-area-bg-glow--left" aria-hidden="true" />

      <header className="tanya-area-header">
        <span className="tanya-area-eyebrow">Where We Serve</span>
        <h2 id="tanya-area-title" className="tanya-area-title">
          Maid Services Across <em>Mumbai</em>
        </h2>
        <p className="tanya-area-subtitle">
          Professional, trusted, and reliable maid services available across Mumbai and nearby locations.
        </p>
      </header>

      <div className="tanya-area-grid">
        {SERVICE_AREAS.map((area) => (
          <AreaCard key={area.id} area={area} onExplore={onExplore} />
        ))}
      </div>
    </section>
  );
}